import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { TIMESHEET_STATUS, Timesheet } from 'src/app/models/Timesheet';

import { EMOJIS } from '../../models/EMOJIS';
import { ChangeSelectedDate, FetchTimesheetByUserAndDate, UpdateTimesheet } from '../timesheets.actions';
import { TimesheetsService } from '../timesheets.service';
import { TimesheetState } from '../timesheets.state';
import { UpdateTimeslotSaveModalComponent } from '../update-timeslot-save-modal/update-timeslot-save-modal.component';
import { finalize } from 'rxjs/operators';
import { EmployeeService } from 'src/app/employees/employees.service';
import { TimesheetAiValidatorService } from '../timesheet-ai-validator.service';
import * as moment from 'moment';
@Component({
    selector: 'app-update-timeslot',
    templateUrl: './update-timeslot.component.html',
    styleUrls: ['./update-timeslot.component.scss'],
})
export class UpdateTimeslotComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();

    //@Select(TimesheetState.timesheets) timesheets$: Observable<Timesheet[]>;
    @Select(TimesheetState.publicTimesheet) timesheet$: Observable<Timesheet>;

    description: string = '';
    descriptionTooLong: boolean = false;
    currentAmountOfExtraInfoCharacters: number = 0;
    descriptionMaxLength: number = 1000;

    happy?: number;
    emojis: EMOJIS[] = [];

    timesheet: Timesheet;
    isLoading: boolean = true;
    error: string = '';
    isSyncing = false;
    singleProjectTimesheetType?: string;
    hasSingleProject = false;
    hasAnyRequiredProject = false;
    singleRequiredType?: string;
    isPublicContext: boolean = false;

    constructor(
        private store: Store,
        private route: ActivatedRoute,
        private timesheetsService: TimesheetsService,
        private modalService: NgbModal,
        private toastr: ToastrService,
        private logger: NGXLogger,
        private employeeService: EmployeeService,
        private tsAiValidator: TimesheetAiValidatorService
    ) {}

    ngOnInit(): void {
        let userId = this.route.snapshot.paramMap.get('userUuid');
        let date = this.route.snapshot.paramMap.get('date');
        let token = this.route.snapshot.paramMap.get('token');
        sessionStorage.setItem('timesheetToken', token);
        this.isPublicContext = !!token;

        this.logger.debug('Fetching timesheets');
        this.subscriptions.add(
            this.store.dispatch(new FetchTimesheetByUserAndDate(userId, date)).subscribe(
                () => {},
                () => {
                    this.isLoading = false;
                    this.error = 'This timesheet is no longer available. It was probably deleted. Contact your administrator if you think this is a mistake.';
                },
            ),
        );

        this.subscriptions.add(
            this.timesheet$.subscribe((timesheet) => {
                if (!timesheet) return;

                this.timesheet = timesheet;
                this.description = this.timesheet.extraInfo ? this.timesheet.extraInfo : '';
                this.currentAmountOfExtraInfoCharacters = this.description.length;
                if (this.timesheet.happyScore) this.happy = this.timesheet.happyScore;
                this.isLoading = false;

                // Compute single-project hint text (non-blocking, subtle)
                const singleProjectUuid = this.getSingleProjectUuid(timesheet);
                this.hasSingleProject = !!singleProjectUuid;
                if (singleProjectUuid) {
                    // Try to read from embedded project first
                    const embeddedType = this.computeSingleProjectType(timesheet);
                    this.singleProjectTimesheetType = embeddedType;
                    // Fallback: fetch linked projects to resolve type if missing
                    if (!embeddedType && this.timesheet?.employee?.uuid) {
                        this.employeeService.getAllLinkedProjects(this.timesheet.employee.uuid).subscribe(
                            (projects) => {
                                const p = (projects || []).find((pr) => pr?.uuid === singleProjectUuid);
                                const t = (p?.timesheetType || '').trim();
                                if (t) this.singleProjectTimesheetType = t;
                            },
                            () => {}
                        );
                    }
                } else {
                    this.singleProjectTimesheetType = undefined;
                }

                // Detect if any selected project requires a timesheet (flag)
                try {
                    const slots = (timesheet?.timeslots || []).filter((s: any) => !!s?.project);
                    const requiredSlots = slots.filter((s: any) => !!s?.project?.timesheetRequired);
                    this.hasAnyRequiredProject = requiredSlots.length > 0;
                    if (this.hasAnyRequiredProject) {
                        const types = requiredSlots
                            .map((s: any) => (s?.project?.timesheetType || '').toString().trim())
                            .filter((v: string) => !!v);
                        const uniqueTypes = Array.from(new Set(types));
                        this.singleRequiredType = uniqueTypes.length === 1 ? uniqueTypes[0] : undefined;
                    } else {
                        this.singleRequiredType = undefined;
                    }
                } catch {
                    this.hasAnyRequiredProject = false;
                    this.singleRequiredType = undefined;
                }
            }),
        );

        this.emojis = Object.values(EMOJIS);
    }

    openReadyForReviewModal() {
        this.logger.debug('Opening confirmationModal');
        const modalRef = this.modalService.open(UpdateTimeslotSaveModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        this.subscriptions.add(
            modalRef.componentInstance.closeEvent.subscribe((val) => {
                let updatedTimesheet = JSON.parse(JSON.stringify(this.timesheet));
                updatedTimesheet.extraInfo = this.description;
                updatedTimesheet.happyScore = this.happy;
                if (val) {
                    updatedTimesheet.status = TIMESHEET_STATUS.REVIEW;
                } else {
                    updatedTimesheet.status = TIMESHEET_STATUS.EDIT;
                }
                this.logger.debug('Attempt to update timesheet');
                this.store.dispatch(new UpdateTimesheet(updatedTimesheet)).subscribe(
                    (res) => {
                        this.logger.debug('Succesfully updated timesheet');
                        if (updatedTimesheet.status === TIMESHEET_STATUS.REVIEW) {
                            this.toastr.success('Timesheet submitted for review.');
                        } else {
                            this.toastr.success('Timesheet saved as draft.');
                        }
                    },
                    (err) => {
                        this.logger.debug('Failed to update timesheet');
                        this.toastr.error('Oops, something went wrong. Please try again later.');
                    },
                );
            }),
        );
    }

    onDescriptionKeyUp() {
        this.descriptionTooLong = false;
        this.currentAmountOfExtraInfoCharacters = this.description.length;
        if (this.currentAmountOfExtraInfoCharacters > this.descriptionMaxLength) {
            this.descriptionTooLong = true;
        }
    }

    isLoggedIn(): boolean {
        return localStorage.getItem('token') != undefined && localStorage.getItem('token') != null;
    }

    getTotalMinutes = Timesheet.getTotalMinutes;
    getTotalDaysFromTimesheet = Timesheet.getTotalDaysFromTimesheet;
    getTotalDaysFromMinutes = Timesheet.getTotalDaysFromMinutes;
    getRequiredHoursDiff = Timesheet.getRequiredHoursDiff;
    getRequiredHoursDiffPercentage = Timesheet.getRequiredHoursDiffPercentage;
    getEffectiveHoursPerDay = Timesheet.getEffectiveHoursPerDay;
    getEffectiveRequiredHours = Timesheet.getEffectiveRequiredHours;

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }


// Optioneel: als je al een loader hebt (bv. loadTimesheet()), gebruik die.
// Anders kun je hier soft refresh doen.
private refreshTimesheetAfterSync(): void {
    this.ngOnInit();
}

private computeSingleProjectType(ts?: Timesheet): string | undefined {
    try {
        const slots = ts?.timeslots || [];
        if (!slots.length) return undefined;
        const ids = slots.map((s: any) => s?.project?.uuid).filter(Boolean);
        const titles = slots.map((s: any) => (s?.project?.title || '').trim()).filter(Boolean);
        const uniqueById = Array.from(new Set(ids));
        const uniqueByTitle = Array.from(new Set(titles));
        const isSingle = uniqueById.length === 1 || (ids.length === 0 && uniqueByTitle.length === 1);
        if (!isSingle) return undefined;
        const project = ids.length
            ? slots.find((s: any) => s?.project?.uuid === uniqueById[0])?.project
            : slots.find((s: any) => (s?.project?.title || '').trim() === uniqueByTitle[0])?.project;
        const type = (project?.timesheetType || '').trim();
        return type || undefined;
    } catch {
        return undefined;
    }
}

private getSingleProjectUuid(ts?: Timesheet): string | undefined {
    try {
        const slots = ts?.timeslots || [];
        if (!slots.length) return undefined;
        const ids = slots.map((s: any) => s?.project?.uuid).filter(Boolean);
        const uniqueIds = Array.from(new Set(ids));
        if (uniqueIds.length === 1) return uniqueIds[0];
        // Fallback when uuids are not present: still signal single project
        const titles = slots.map((s: any) => (s?.project?.title || '').trim()).filter(Boolean);
        const uniqueTitles = Array.from(new Set(titles));
        return uniqueIds.length === 0 && uniqueTitles.length === 1 ? '' : undefined;
    } catch {
        return undefined;
    }
}

isReviewStatus(ts?: any): boolean {
        const rawStatus = ts?.status?.key ?? ts?.status?.displayName ?? ts?.status ?? '';
        return rawStatus.toString().trim().toUpperCase() === 'REVIEW';
}

syncWithJira(timesheet: { uuid: string }): void {
  if (!timesheet?.uuid) {
    this.toastr.error('Timesheet ID ontbreekt.');
    return;
  }

        if (this.isReviewStatus(this.timesheet || timesheet)) {
        this.toastr.warning('Jira sync is disabled when the timesheet is in review.');
        return;
    }

  this.logger.debug('Attempting Jira sync for timesheet', timesheet.uuid);
  this.isSyncing = true;

  this.timesheetsService
    .syncJira(timesheet.uuid)     // zelfde service-aanroep als in je lijstcomponent
    .pipe(finalize(() => (this.isSyncing = false)))
    .subscribe({
      next: () => {
        this.logger.debug('Jira sync succesvol', timesheet.uuid);
        this.toastr.success('Jira sync voltooid.');
        this.refreshTimesheetAfterSync();
      },
      error: (err) => {
        this.logger.error('Jira sync mislukt', err);
        this.toastr.error('Oops, something went wrong. Please try again later.');
      }
    });
}

// ===== AI validation for uploaded timesheets (attachments) =====
isValidatingAttachment: Record<string, boolean> = {};
attachmentValidationResults: Record<string, { ok: boolean; issues: string[]; type?: string; details?: { parsedHours?: number | null; pdfMonthISO?: string | null; expectedMonthISO?: string | null; hoursOk?: boolean; monthOk?: boolean; signed?: boolean; employeePresent?: boolean } }> = {};

    // Header progress for global validation
    headerValidationProgress = 0;
    headerValidationTimer?: any;

validateTimesheet(): void {
    try {
        if (!this.timesheet) return;
        const attachments = (this.timesheet.timeslots || []).filter(ts => !!ts.pdfUrl);
        if (attachments.length === 0) {
            this.toastr.info('Geen timesheet PDF gevonden om te valideren.');
            return;
        }
        this.logger.info('[TIMESHEET] Starting AI validation for', attachments.length, 'attachments');
            this.startHeaderProgress();
        const uploadedMonthISO = this.timesheet?.expirationDate
            ? moment(this.timesheet.expirationDate).startOf('month').format('YYYY-MM-DD')
            : moment().startOf('month').format('YYYY-MM-DD');
        const employeeName = this.timesheet?.employee ? `${this.timesheet.employee.firstName} ${this.timesheet.employee.lastName}` : '';

        const validations = attachments.map((timeslot) => this.runAttachmentValidation(timeslot, { uploadedMonthISO, employeeName }));
        Promise.all(validations).then(() => {
            const issuesTotal = Object.values(this.attachmentValidationResults || {}).reduce((acc, r) => acc + ((r.issues || []).length), 0);
            if (issuesTotal > 0) {
                this.toastr.warning(`AI-validatie klaar: ${issuesTotal} mogelijke probleem(en) gevonden.`);
            } else {
                this.toastr.success('AI-validatie klaar: geen problemen gevonden.');
            }
                this.stopHeaderProgress();
        });
    } catch (e) {
        this.logger.warn('validateTimesheet failed', e);
        this.toastr.error('AI-validatie kon niet worden uitgevoerd.');
            this.stopHeaderProgress();
    }
}

private runAttachmentValidation(timeslot: any, ctx: { uploadedMonthISO: string; employeeName: string }): Promise<void> {
    return new Promise((resolve) => {
        try {
            const pdfUrl = timeslot?.pdfUrl || '';
            if (!pdfUrl) return resolve();
            const workedHoursRaw = (Number(timeslot?.minutes || 0) + Number(timeslot?.leaveMinutes || 0)) / 60;
            const daysWorked = workedHoursRaw / 8;
            const invoiceByDay = !!timeslot?.project?.invoiceByDay;
            const hoursPerDay = Number(timeslot?.project?.hoursPerDay ?? 8);
            const expectedHours = invoiceByDay
                ? Number((daysWorked * hoursPerDay).toFixed(2))
                : Number(workedHoursRaw.toFixed(2));
            const monthLabel = undefined; // not tracked here
            const timesheetTypeHint = (timeslot?.project?.timesheetType || '').toString().trim();

            this.logger.info('[TIMESHEET] AI validate attachment', {
                uuid: timeslot?.uuid,
                pdfUrl,
                employeeName: ctx.employeeName,
                expectedHours,
                uploadedMonthISO: ctx.uploadedMonthISO,
                timesheetTypeHint
            });
            this.isValidatingAttachment[timeslot.uuid] = true;
            this.tsAiValidator
                .validate(pdfUrl, {
                    employeeName: ctx.employeeName,
                    expectedHours,
                    monthLabel,
                    uploadedMonthISO: ctx.uploadedMonthISO,
                    timesheetTypeHint,
                })
                .then((result) => {
                    this.attachmentValidationResults[timeslot.uuid] = {
                        ok: result.ok,
                        issues: result.issues || [],
                        type: result.type,
                        details: result.details || {}
                    };

                    // Persist compact validation to backend so it appears on public page
                    const payload = {
                        consultantName: ctx.employeeName || undefined,
                        timesheetType: (result.type || timesheetTypeHint || '').trim() || undefined,
                        periodISO: (result.details?.pdfMonthISO as any) || ctx.uploadedMonthISO || undefined,
                        signed: result.details?.signed,
                        hours: typeof result.details?.parsedHours === 'number' ? result.details.parsedHours : expectedHours
                    };
                    this.timesheetsService.saveTimeslotValidation(timeslot.uuid, payload).subscribe({
                        next: (updated) => {
                            const refreshed = updated?.timeslots?.find(t => t.uuid === timeslot.uuid);
                            if (refreshed) {
                                timeslot.validatedConsultantName = refreshed.validatedConsultantName;
                                timeslot.validatedTimesheetType = refreshed.validatedTimesheetType;
                                timeslot.validatedPeriodISO = refreshed.validatedPeriodISO;
                                timeslot.validatedSigned = refreshed.validatedSigned;
                                timeslot.validatedHours = refreshed.validatedHours;
                            }
                        },
                        error: (err) => {
                            this.logger.warn('Opslaan validatie mislukt', err);
                        }
                    });
                })
                .catch((err) => {
                    this.logger.warn('AI validation failed', err);
                    this.attachmentValidationResults[timeslot.uuid] = { ok: false, issues: ['Validatie mislukt door een fout.'] };
                })
                .finally(() => {
                    this.isValidatingAttachment[timeslot.uuid] = false;
                    resolve();
                });
        } catch (e) {
            this.logger.warn('runAttachmentValidation error', e);
            resolve();
        }
    });
}

isAnyValidationInProgress(): boolean {
    try {
        const slots = (this.timesheet?.timeslots || []).filter(ts => !!ts.pdfUrl);
        return slots.some(a => !!this.isValidatingAttachment[a.uuid!]);
    } catch {
        return false;
    }
}

hasAnyTimesheetPdf(): boolean {
    try {
        return (this.timesheet?.timeslots || []).some(ts => !!ts.pdfUrl);
    } catch {
        return false;
    }
}

hasAnyValidationPresent(): boolean {
    try {
        return (this.timesheet?.timeslots || []).some(ts => (
            !!ts?.validatedConsultantName || !!ts?.validatedTimesheetType || !!ts?.validatedPeriodISO ||
            ts?.validatedSigned !== undefined || ts?.validatedHours !== undefined
        ));
    } catch {
        return false;
    }
}

hasValidationIssues(): boolean {
    try {
        return Object.values(this.attachmentValidationResults || {}).some(r => !r.ok && (r.issues || []).length > 0);
    } catch {
        return false;
    }
}

private startHeaderProgress(): void {
    this.headerValidationProgress = 10;
    this.headerValidationTimer = setInterval(() => {
        this.headerValidationProgress = Math.min(95, (this.headerValidationProgress || 10) + 7);
    }, 300);
}

private stopHeaderProgress(): void {
    if (this.headerValidationTimer) clearInterval(this.headerValidationTimer);
    this.headerValidationProgress = 100;
    setTimeout(() => { this.headerValidationProgress = 0; }, 800);
}

getValidationChecks(): Array<{ fileName: string; statuses: Array<{ label: string; value: 'ok' | 'fout' | 'onbekend'; display: string }> }> {
    try {
        const results: Array<{ fileName: string; statuses: Array<{ label: string; value: 'ok' | 'fout' | 'onbekend'; display: string }> }> = [];
        const attachments = (this.timesheet?.timeslots || []).filter(ts => !!ts.pdfUrl);
        attachments.forEach((timeslot) => {
            const res = this.attachmentValidationResults[timeslot.uuid!];
            const statuses: Array<{ label: string; value: 'ok' | 'fout' | 'onbekend'; display: string }> = [];

            // Hours
            const parsedHours = res?.details?.parsedHours;
            const workedHoursRaw = (Number(timeslot?.minutes || 0) + Number(timeslot?.leaveMinutes || 0)) / 60;
            const daysWorked = workedHoursRaw / 8;
            const expectedHours = (timeslot?.project?.invoiceByDay)
                ? Number((daysWorked * (timeslot?.project?.hoursPerDay ?? 8)).toFixed(2))
                : Number(workedHoursRaw.toFixed(2));
            let hoursVal: 'ok' | 'fout' | 'onbekend' = 'onbekend';
            if (typeof res?.details?.hoursOk === 'boolean') {
                hoursVal = res.details.hoursOk ? 'ok' : 'fout';
            } else if (typeof parsedHours === 'number' && typeof expectedHours === 'number') {
                hoursVal = Math.abs(parsedHours - expectedHours) < 0.51 ? 'ok' : 'fout';
            }
            const hoursDisplay = `${typeof parsedHours === 'number' ? parsedHours.toFixed(0) : '?'}` +
                                                     ` van ${typeof expectedHours === 'number' ? expectedHours.toFixed(0) : '?'}`;
            statuses.push({ label: 'Uren', value: hoursVal, display: hoursDisplay });

            // Month
            const pdfMonthISO = res?.details?.pdfMonthISO as string | undefined;
            const expectedMonthISO = res?.details?.expectedMonthISO as string | undefined;
            let monthVal: 'ok' | 'fout' | 'onbekend' = 'onbekend';
            if (typeof res?.details?.monthOk === 'boolean') {
                monthVal = res.details.monthOk ? 'ok' : 'fout';
            } else if (pdfMonthISO && (expectedMonthISO || this.timesheet?.expirationDate)) {
                try {
                    const refDate = expectedMonthISO ? expectedMonthISO : this.timesheet?.expirationDate;
                    const up = (refDate ? moment(refDate).format('MMMM YYYY') : '').toLowerCase();
                    const isoYear = String(pdfMonthISO).slice(0, 4);
                    const isoMonth = String(pdfMonthISO).slice(5, 7);
                    const monthNames = ['january','february','march','april','may','june','july','august','september','october','november','december'];
                    const mName = monthNames[(Number(isoMonth) - 1) || 0];
                    monthVal = (up.includes(mName) && up.includes(isoYear)) ? 'ok' : 'fout';
                } catch { monthVal = 'onbekend'; }
            }
            const monthDisplay = pdfMonthISO
                ? moment(pdfMonthISO).format('MMMM YYYY')
                : (expectedMonthISO
                        ? moment(expectedMonthISO).format('MMMM YYYY')
                        : (this.timesheet?.expirationDate ? moment(this.timesheet.expirationDate).format('MMMM YYYY') : 'onbekend'));
            statuses.push({ label: 'Maand', value: monthVal, display: monthDisplay });

            // Name present
            let nameVal: 'ok' | 'fout' | 'onbekend' = 'onbekend';
            if (typeof res?.details?.employeePresent === 'boolean') {
                nameVal = res.details.employeePresent ? 'ok' : 'fout';
            } else {
                const hasNameIssue = (res?.issues || []).some((s: string) => s.toLowerCase().includes('naam') && s.toLowerCase().includes('niet'));
                nameVal = hasNameIssue ? 'fout' : 'onbekend';
            }
            const nameDisplay = this.timesheet?.employee ? `${this.timesheet.employee.firstName} ${this.timesheet.employee.lastName}` : 'onbekend';
            statuses.push({ label: 'Naam', value: nameVal, display: nameDisplay });

            // Signature
            let signVal: 'ok' | 'fout' | 'onbekend' = 'onbekend';
            if (typeof res?.details?.signed === 'boolean') {
                signVal = res.details.signed ? 'ok' : 'fout';
            } else {
                const hasSignIssue = (res?.issues || []).some((s: string) => s.toLowerCase().includes('niet ondertekend'));
                signVal = hasSignIssue ? 'fout' : 'onbekend';
            }
            const signDisplay = signVal === 'ok' ? 'aanwezig' : (signVal === 'fout' ? 'ontbreekt' : 'onbekend');
            statuses.push({ label: 'Handtekening', value: signVal, display: signDisplay });

            // Type match
            const detectedType = (res?.type || '').toString().trim();
            const hint = (timeslot?.project?.timesheetType || '').toString().trim();
            let typeVal: 'ok' | 'fout' | 'onbekend' = 'onbekend';
            if (detectedType) {
                typeVal = hint && detectedType.toLowerCase() === hint.toLowerCase() ? 'ok' : 'fout';
            }
            const typeDisplay = detectedType || (hint || 'onbekend');
            statuses.push({ label: 'Type', value: typeVal, display: typeDisplay });

            const fileName = (timeslot?.pdfUrl || '').split('/').pop() || 'bijlage';
            results.push({ fileName, statuses });
        });
        return results;
    } catch {
        return [];
    }
}

}
